import { observable, action, computed, runInAction } from "mobx";

const IS_BIRD_APP = process.env.REACT_APP_ENV === "bird";

export default class BirdDataTransferStore {
  @observable arr_all_frameworks = [];
  @observable arr_all_finalReleases = [];
  @observable arr_all_otherBranches = [];
  @observable arr_all_additional_frameworks = [];
  @observable arr_all_additional_maintenance_agencies = [];
  @observable selectedFrameworks = [];
  @observable includeMappingContent = true;
  @observable includeRenderingContent = true;
  @observable includeTransformationContent = true;
  @observable selectedFinalReleases = [];
  @observable selectedOtherBranches = [];
  @observable versionTitle = "";
  @observable versionDescription = "";

  @observable dryRun = true;

  @observable transferringData = false;

  @observable transferResult = "";
  @observable transferMessage = "";
  @observable transferDetails = "";
  @observable maximumDescriptionLength = 500;
  @observable maximumTitleLength = 100;

  constructor(http, userStore) {
    this.http = http;
    this.userStore = userStore;
  }

  @action
  func_reset = () => {
    this.arr_all_frameworks = [];
    this.arr_all_finalReleases = [];
    this.arr_all_otherBranches = [];
    this.arr_all_additional_frameworks = [];
    this.arr_all_additional_maintenance_agencies = [];
    this.selectedFrameworks = [];
    this.includeMappingContent = true;
    this.includeRenderingContent = true;
    this.includeTransformationContent = true;
    this.selectedFinalReleases = [];
    this.selectedOtherBranches = [];
    this.versionTitle = "";
    this.versionDescription = "";

    this.dryRun = true;

    this.transferResult = "";
    this.transferMessage = "";
    this.transferDetails = "";
    this.fetch_data();
  };

  @action
  fetch_data = () => {
    if (IS_BIRD_APP) return;

    if (!this.userStore.isUserSystemAdministrator) return;

    this.func_fetch_all_frameworks();
    this.func_fetch_all_maintenance_agencies();
    this.func_fetch_all_final_releases();
    this.func_fetch_all_other_branches();
  };

  @action
  func_fetch_all_frameworks = () => {
    this.http.service_query_all_Frameworks().then(
      action((frameworks) => {
        if (frameworks?.results) {
          this.arr_all_frameworks = frameworks.results.map((framework) => ({
            label: framework.name,
            value: framework.frameworkId,
          }));
          this.arr_all_additional_frameworks = frameworks.results.map(
            (framework) => ({
              label: framework.name,
              value: framework.frameworkId,
            })
          );
        } else {
          this.arr_all_frameworks = [];
          this.arr_all_additional_frameworks = [];
        }
      })
    );
  };

  @action
  func_fetch_all_maintenance_agencies = () => {
    this.http.service_query_all_Maintenance_Agencies().then(
      action((maintenanceAgencies) => {
        if (maintenanceAgencies.results) {
          this.arr_all_additional_maintenance_agencies =
            maintenanceAgencies.results.map((maintenance_agency) => ({
              label: maintenance_agency.name,
              value: maintenance_agency.maintenanceAgencyId,
            }));
        } else {
          this.arr_all_additional_maintenance_agencies = [];
        }
      })
    );
  };

  @action
  func_fetch_all_final_releases = () => {
    const branchesFilter = {
      type: "Branch",
      filter: {
        branchType: {
          in: ["FINAL"],
        },
        status: {
          in: ["ACTIVE"],
        },
        type: "Branch",
      },
    };
    this.http.service_fetch_filteredEntities(branchesFilter, (response) => {
      if (response && response.results) {
        response.results.forEach((finalRelease) => {
          this.arr_all_finalReleases.push({
            label: finalRelease.name,
            value: finalRelease.tid,
          });
        });
      } else {
        this.arr_all_finalReleases = [];
      }
    });
  };
  @action
  func_fetch_all_other_branches = () => {
    const branchesFilter = {
      type: "Branch",
      filter: {
        branchType: {
          in: ["BRANCH", "INTERMEDIATE"],
        },
        status: {
          in: ["ACTIVE"],
        },
        type: "Branch",
      },
    };
    this.http.service_fetch_filteredEntities(branchesFilter, (response) => {
      if (response && response.results) {
        response.results.forEach((otherBranch) => {
          this.arr_all_otherBranches.push({
            label: otherBranch.name,
            value: otherBranch.tid,
          });
        });
      } else {
        this.arr_all_otherBranches = [];
      }
    });
  };

  @action
  handleSelectedFrameworksChange = (frameworks) => {
    this.selectedFrameworks = frameworks;
  };

  @action
  toggleIncludeMappingContent = () => {
    this.includeMappingContent = !this.includeMappingContent;
  };

  @action
  toggleIncludeRenderingContent = () => {
    this.includeRenderingContent = !this.includeRenderingContent;
  };
  
  @action
  toggleIncludeTransformationContent = () => {
    this.includeTransformationContent = !this.includeTransformationContent;
  };

  @action
  handleSelectedFinalReleasesChange = (finalReleases) => {
    this.selectedFinalReleases = finalReleases;
  };

  @action
  handleSelectedOtherBranchesChange = (otherBranches) => {
    this.selectedOtherBranches = otherBranches;
  };

  @action
  handleVersionTitleChange = (event) => {
    this.setVersionTitle(event.target.value);
  };

  @action
  setVersionTitle = (versionTitle) => {
    this.versionTitle = versionTitle;
  };

  @action
  handleVersionDescriptionChange = (event) => {
    this.setVersionDescription(event.target.value);
  };

  @action
  setVersionDescription = (versionDescription) => {
    this.versionDescription = versionDescription;
  };

  @action
  toggleDryRun = () => {
    this.dryRun = !this.dryRun;
  };

  @action
  func_handleTransferClick = async () => {
    let birdTransferParameters = {
      frameworkIds: this.selectedFrameworks,
      includeMapping: this.includeMappingContent,
      includeRendering: this.includeRenderingContent,
      includeTransformation: this.includeTransformationContent,
      finalReleaseTids: this.selectedFinalReleases,
      otherBranchTids: this.selectedOtherBranches,
      versionTitle: this.versionTitle,
      versionDescription: this.versionDescription,
      dryRun: this.dryRun,
    };

    if (this.controllerForMultiSearchQuery && this.transferringData) {
      this.controllerForMultiSearchQuery.abort();
    }

    const controller = new window.AbortController();
    const signal = controller.signal;
    this.controllerForMultiSearchQuery = controller;

    try {
      this.transferringData = true;
      const json = await this.http.transferBirdData(
        birdTransferParameters,
        signal
      );
      if (
        json.error ||
        (json.errorMessage !== null && json.errorMessage !== undefined)
      ) {
        this.transferringData = false;
        return;
      }
      runInAction(() => {
        this.transferResult = json.result;
        this.transferMessage = json.resultMessage;
        this.transferDetails = json;
      });
      this.transferringData = false;
    } catch (error) {
      if (error.name === "AbortError") {
        console.log("BIRD data transfer aborted");
      } else {
        console.error("Error getting result.", error);
        this.transferringData = false;
      }
    }
  };
  getMaximumDescriptionLength() {
    return this.maximumDescriptionLength;
  }

  @computed
  get getSelectedFrameworks() {
    return this.selectedFrameworks;
  }

  @computed
  get getSelectedFinalReleases() {
    return this.selectedFinalReleases;
  }

  @computed
  get getSelectedOtherBranches() {
    return this.selectedOtherBranches;
  }

  @computed
  get shouldIncludeMappingContent() {
    return this.includeMappingContent;
  }

  @computed
  get shouldIncludeRenderingContent() {
    return this.includeRenderingContent;
  }

  @computed
  get shouldIncludeTransformationContent() {
    return this.includeTransformationContent;
  }

  @computed
  get getVersionTitle() {
    return this.versionTitle;
  }

  @computed
  get getVersionDescription() {
    return this.versionDescription;
  }

  @computed
  get shouldBeDryRun() {
    return this.dryRun;
  }

  @computed
  get get_arr_frameworks() {
    return this.arr_all_frameworks;
  }

  @computed
  get get_arr_additional_frameworks() {
    return this.arr_all_additional_frameworks;
  }

  @computed
  get get_arr_additional_maintenance_agencies() {
    return this.arr_all_additional_maintenance_agencies;
  }

  @computed
  get get_arr_finalReleases() {
    return this.arr_all_finalReleases;
  }

  @computed
  get get_arr_otherBranches() {
    return this.arr_all_otherBranches;
  }
}
